/**
 * Where the media encoder is with an uploaded video or audio file. Only those
 * carry one — anything else is absent on the wire and `null` here, which means
 * "nothing to wait for".
 *
 * While `pending` every service answers the file's bytes with a 409: the upload is
 * being re-encoded into a copy every browser plays, and that copy replaces the
 * original under the same link. `ready` files have also been renamed to the new
 * extension and changed type and size; `failed` ones are served as uploaded.
 */
export type MediaStatus = 'failed' | 'pending' | 'ready';

/** `cover` is a still of a video, or a song's album art. More sizes may follow. */
export const MEDIA_COVER_VARIANT = 'cover';

/**
 * `backup` is an H.264 + AAC MP4 of a video, written beside the main rendition
 * (HEVC MP4 by default, VP9 WebM as an option) for browsers that cannot play it.
 * Optional: absent when the encoder was told not to write one, or it failed.
 */
export const MEDIA_BACKUP_VARIANT = 'backup';

/**
 * Downscaled webp copies of an uploaded image. `thumb` is a 192×192 centre crop;
 * `small` and `medium` fit within 640 and 1280 px, aspect ratio kept. Any of them
 * may be absent — each upload site stores only the sizes it is shown at, the
 * source was already smaller, the file predates them, or it is not an image —
 * and then `link` (a webp within 2560×2560) is the best fit.
 */
export const IMAGE_THUMB_VARIANT = 'thumb';
export const IMAGE_SMALL_VARIANT = 'small';
export const IMAGE_MEDIUM_VARIANT = 'medium';

const IMAGE_VARIANTS = [IMAGE_THUMB_VARIANT, IMAGE_SMALL_VARIANT, IMAGE_MEDIUM_VARIANT] as const;

export type MediaVariantDto = {
  content_type: string;
  width?: number;
  height?: number;
  size?: number;
  /** Only activity links its variants; the others are `<file link>/variants/<name>`. */
  link?: string;
};

export type MediaVariant = {
  contentType: string;
  width?: number;
  height?: number;
  size?: number;
  link: string;
};

export type MediaVariants = Record<string, MediaVariant>;

/** How often a screen with a file still being processed asks for it again. */
export const MEDIA_POLL_INTERVAL = 5_000;

export const isMediaPending = (status: MediaStatus | null | undefined) => status === 'pending';

const withoutQuery = (link: string) => link.split('?')[0];

export const mapMediaVariants = (
  fileLink: string | null | undefined,
  variants: Record<string, MediaVariantDto> | null | undefined,
): MediaVariants => {
  const mapped: MediaVariants = {};
  if (!fileLink || !variants) return mapped;

  for (const [name, { content_type, width, height, size, link }] of Object.entries(variants)) {
    mapped[name] = {
      contentType: content_type,
      width,
      height,
      size,
      link: link ?? `${withoutQuery(fileLink)}/variants/${name}`,
    };
  }
  return mapped;
};

/** The cover's private link, or null while there is none (a pending file never has one). */
export const coverLink = (variants: MediaVariants | null | undefined) =>
  variants?.[MEDIA_COVER_VARIANT]?.link ?? null;

/** The backup rendition's private link, or null while there is none. */
export const backupLink = (variants: MediaVariants | null | undefined) =>
  variants?.[MEDIA_BACKUP_VARIANT]?.link ?? null;

/**
 * A `refetchInterval` that keeps asking while the data holds a file still being
 * processed and stops once none is. Document and activity push nothing when the
 * encoder finishes, so asking again is how a screen hears about it.
 */
export const pollWhileMediaPending =
  <T>(hasPending: (data: T) => boolean) =>
  (query: { state: { data: T | undefined } }) =>
    query.state.data !== undefined && hasPending(query.state.data) ? MEDIA_POLL_INTERVAL : false;

export const hasPendingMedia = (files: readonly { mediaStatus?: MediaStatus | null }[]) =>
  files.some((file) => isMediaPending(file.mediaStatus));

type ImageVariantSource = { width?: number; height?: number; link?: string };

/** Anything shaped like a stored file — a wire `FileDto` or a mapped model. */
export type ImageFile = {
  link?: null | string;
  variants?: null | Record<string, ImageVariantSource>;
};

const MAX_PIXEL_RATIO = 3;

/**
 * The smallest link that still fills a `width`×`height` CSS px box drawn with
 * `object-fit: cover`, or the original when no variant is big enough. Only a
 * square box may take the centre-cropped `thumb`. A `height` of 0 is a box as
 * wide as `width` whose height follows the image.
 */
export const imageVariantLink = (
  file: ImageFile | null | undefined,
  width: number,
  height = width,
): null | string => {
  if (!file?.link) return null;
  const { link, variants } = file;
  if (!variants) return link;

  const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
  const neededWidth = Math.ceil(width * ratio);
  const neededHeight = Math.ceil(height * ratio);

  let best: { area: number; link: string } | null = null;
  for (const name of IMAGE_VARIANTS) {
    const variant = variants[name];
    if (!variant?.width || !variant.height) continue;
    if (name === IMAGE_THUMB_VARIANT && width !== height) continue;
    if (variant.width < neededWidth || variant.height < neededHeight) continue;

    const area = variant.width * variant.height;
    if (!best || area < best.area) {
      best = { area, link: variant.link ?? `${withoutQuery(link)}/variants/${name}` };
    }
  }
  return best?.link ?? link;
};
